import React, { useEffect, useRef } from 'react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import './AnimatedSlogan.css';
import bubbleTeaImage from '../assets/designbubletea.png.avif';
import smoothieImage from '../assets/designsmothie.png.avif';

gsap.registerPlugin(ScrollTrigger);

const AnimatedSlogan = () => {
  const containerRef = useRef(null);
  const backgroundRef = useRef(null);
  const textRef = useRef(null);
  const circlesRef = useRef([]);


  useEffect(() => {
    const container = containerRef.current;
    const background = backgroundRef.current;
    const text = textRef.current;
    const circles = circlesRef.current;


    // Timeline principale
    const tl = gsap.timeline({
      scrollTrigger: {
        trigger: container,
        start: "top 80%",
        end: "bottom 20%",
        toggleActions: "play none none reverse"
      }
    });

    // Animation des cercles concentriques
    circles.forEach((circle, index) => {
      if (circle) {
        // Animation initiale d'apparition
        gsap.set(circle, { 
          scale: 0,
          opacity: 0 
        });

        tl.to(circle, {
          scale: 1,
          opacity: 1,
          duration: 1.5,
          ease: "elastic.out(1, 0.5)",
          delay: index * 0.2
        }, 0);

        // Animation continue de rotation et pulsation
        gsap.to(circle, {
          rotation: 360,
          duration: 20 + (index * 5),
          repeat: -1,
          ease: "none"
        });

        gsap.to(circle, {
          scale: 1.05,
          duration: 3 + (index * 0.5),
          repeat: -1,
          yoyo: true,
          ease: "sine.inOut"
        });
      }
    });

    // Ne pas manipuler le DOM ici - le texte est déjà structuré via React

    // Animation des lettres
    const letters = text.querySelectorAll('.letter');
    const iconWords = text.querySelectorAll('.icon-word');

    // S'assurer que les éléments existent avant d'animer
    if (letters.length > 0) {
      // Filtrer pour exclure les lettres du mot "sucré"
      const lettersToAnimate = Array.from(letters).filter(letter => {
        const word = letter.closest('.word');
        return !word || word.textContent !== 'sucré';
      });

      // Afficher directement le mot "sucré" sans animation
      const sucreLetters = Array.from(letters).filter(letter => {
        const word = letter.closest('.word');
        return word && word.textContent === 'sucré';
      });

      // Mot "sucré" visible immédiatement, sans mouvement
      if (sucreLetters.length > 0) {
        gsap.set(sucreLetters, { 
          y: 0, 
          opacity: 1,
          scale: 1
        });
      }

      // Animer seulement les autres lettres
      if (lettersToAnimate.length > 0) {
        gsap.set(lettersToAnimate, { 
          y: 30, 
          opacity: 0,
          scale: 0.8,
          transformOrigin: "center center",
          clearProps: "all"
        });

        // Animation séquentielle des lettres (sauf "sucré")
        tl.to(lettersToAnimate, {
          y: 0,
          opacity: 1,
          scale: 1,
          duration: 0.6,
          stagger: 0.02,
          ease: "power2.out"
        }, 0.3);
      }
    }

    if (iconWords.length > 0) {
      gsap.set(iconWords, { 
        scale: 0, 
        opacity: 0,
        rotation: -180,
        transformOrigin: "center center"
      });

      // Animation des icônes avec un délai
      tl.to(iconWords, {
        scale: 1,
        opacity: 1,
        rotation: 0,
        duration: 0.6,
        stagger: 0.2,
        ease: "elastic.out(1, 0.5)"
      }, 1);
    }

    // Animation continue de hover sur les icônes seulement
    iconWords.forEach((icon, index) => {
      // Vérifier que c'est bien un emoji avant d'animer
      if (icon.textContent.includes('🧋') || icon.textContent.includes('☁️')) {
        gsap.to(icon, {
          y: -8,
          duration: 2 + (index * 0.3),
          repeat: -1,
          yoyo: true,
          ease: "sine.inOut",
          delay: index * 0.5
        });

        // Effet de rotation subtile seulement pour les emojis
        gsap.to(icon, {
          rotation: 5,
          duration: 3 + (index * 0.5),
          repeat: -1,
          yoyo: true,
          ease: "sine.inOut",
          delay: index * 0.7
        });
      }
    });

    // Animation de parallax au scroll
    gsap.to(background, {
      yPercent: -20,
      ease: "none",
      scrollTrigger: {
        trigger: container,
        start: "top bottom",
        end: "bottom top",
        scrub: 1
      }
    });

    // Nettoyage
    return () => {
      ScrollTrigger.getAll().forEach(trigger => {
        if (trigger.trigger === container) {
          trigger.kill();
        }
      });
    };
  }, []);

  return (
    <section className="animated-slogan" ref={containerRef}>
      <div className="slogan-background" ref={backgroundRef}>
        {/* Cercles concentriques animés */}
        <div className="concentric-circles">
          {[...Array(5)].map((_, index) => (
            <div
              key={index}
              className={`circle circle-${index + 1}`}
              ref={el => circlesRef.current[index] = el}
            />
          ))}
        </div>
      </div>
      
      <div className="slogan-content">
        <h2 className="slogan-text" ref={textRef}>
          <span className="word">
            <span className="letter">d</span>
            <span className="letter">é</span>
            <span className="letter">c</span>
            <span className="letter">o</span>
            <span className="letter">u</span>
            <span className="letter">v</span>
            <span className="letter">r</span>
            <span className="letter">e</span>
            <span className="letter">z</span>
          </span>
          {' '}
          <span className="word">
            <span className="letter">n</span>
            <span className="letter">o</span>
            <span className="letter">t</span>
            <span className="letter">r</span>
            <span className="letter">e</span>
          </span>
          {' '}
          <span className="word icon-word">
            <img src={bubbleTeaImage} alt="Bubble Tea" className="icon-image" />
          </span>
          {' '}
          <span className="word">
            <span className="letter">p</span>
            <span className="letter">a</span>
            <span className="letter">r</span>
            <span className="letter">a</span>
            <span className="letter">d</span>
            <span className="letter">i</span>
            <span className="letter">s</span>
          </span>
          {' '}
          <span className="word">
            <span className="letter">s</span>
            <span className="letter">u</span>
            <span className="letter">c</span>
            <span className="letter">r</span>
            <span className="letter">é</span>
          </span>
          {' '}
          <span className="word">
            <span className="letter">a</span>
            <span className="letter">v</span>
            <span className="letter">e</span>
            <span className="letter">c</span>
          </span>
          {' '}
          <span className="word">
            <span className="letter">d</span>
            <span className="letter">e</span>
            <span className="letter">s</span>
          </span>
          {' '}
          <span className="word">
            <span className="letter">s</span>
            <span className="letter">a</span>
            <span className="letter">v</span>
            <span className="letter">e</span>
            <span className="letter">u</span>
            <span className="letter">r</span>
            <span className="letter">s</span>
          </span>
          {' '}
          <span className="word icon-word">
            <img src={smoothieImage} alt="Smoothie" className="icon-image" />
          </span>
          {' '}
          <span className="word">
            <span className="letter">i</span>
            <span className="letter">n</span>
            <span className="letter">c</span>
            <span className="letter">r</span>
            <span className="letter">o</span>
            <span className="letter">y</span>
            <span className="letter">a</span>
            <span className="letter">b</span>
            <span className="letter">l</span>
            <span className="letter">e</span>
            <span className="letter">s</span>
          </span>
        </h2>
      </div>
    </section>
  );
};

export default AnimatedSlogan;